export const APP_TIME_ZONE = 'Europe/Amsterdam';

const DAY_MS = 86400000;

const getDateParts = (date = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: APP_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date);

    return Object.fromEntries(parts.map(({ type, value }) => [type, value]));
};

export const getDateValue = (offsetDays = 0) => {
    const parts = getDateParts();
    const baseDate = new Date(
        Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            12
        ) + offsetDays * DAY_MS
    );
    const targetParts = getDateParts(baseDate);

    return `${targetParts.year}-${targetParts.month}-${targetParts.day}`;
};

export const getTodayDate = () => getDateValue(0);

export const getTomorrowDate = () => getDateValue(1);

export const getCurrentTimeInMinutes = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: APP_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

    return Number(values.hour) * 60 + Number(values.minute);
};

export const getMillisecondsUntilNextDay = () => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: APP_TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    const elapsedSeconds =
        Number(values.hour) * 3600 +
        Number(values.minute) * 60 +
        Number(values.second);

    return Math.max(1000, (86400 - elapsedSeconds) * 1000);
};

export const getDateOptions = (availableDates = null) => {
    const options = [];

    // Generate 7 days (today + 6 more days) to match Cineville's actual availability
    for (let i = 0; i < 7; i++) {
        const value = getDateValue(i);

        // If availableDates is provided, filter out dates that aren't available
        if (availableDates && !availableDates.includes(value)) {
            continue;
        }

        let label;
        if (i === 0) {
            label = "Today";
        } else if (i === 1) {
            label = "Tomorrow";
        } else {
            // For other days, show day name + date
            const labelDate = new Date(`${value}T12:00:00Z`);
            const dayName = new Intl.DateTimeFormat('en-GB', {
                timeZone: APP_TIME_ZONE,
                weekday: 'long'
            }).format(labelDate);
            const dayNumber = Number(value.split('-')[2]);
            const monthName = new Intl.DateTimeFormat('en-GB', {
                timeZone: APP_TIME_ZONE,
                month: 'short'
            }).format(labelDate);

            label = `${dayName} ${dayNumber} ${monthName}`;
        }

        options.push({ label, value });
    }

    return options;
};

// Helper function to get all available dates from your API
export const getAvailableDates = async (city) => {
    const availableDates = [];

    // Check each of the 7 days
    for (let i = 0; i < 7; i++) {
        const dateValue = getDateValue(i);

        try {
            // Replace this with your actual API call
            const response = await fetch(`/api/movies?date=${dateValue}&city=${city}`);
            const data = await response.json();

            // Check if there are any movies for this date
            if (data && data.length > 0) {
                availableDates.push(dateValue);
            }
        } catch (error) {
            console.error(`Error checking availability for ${dateValue}:`, error);
            // Include the date anyway if there's an error to avoid breaking the UI
            availableDates.push(dateValue);
        }
    }

    return availableDates;
};
